import React, { useState, useMemo } from "react";
import { FiChevronDown, FiChevronUp, FiTrash2 } from "react-icons/fi";
import { PerformanceBadge } from "../PerformanceBadge";
import { getBandForScore } from "../../constants/performance-bands";
import type { SupplierRating } from "../../types/supplier-performance.types";

interface RatingCardProps {
	canDelete?: boolean;
	rating: SupplierRating;
	onDeleteClick?: (rating: SupplierRating) => void;
}

export const RatingCard: React.FC<RatingCardProps> = ({
	rating,
	canDelete = false,
	onDeleteClick,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const raterName = rating.rater
		? `${rating.rater.first_name} ${rating.rater.last_name}`
		: "Unknown";

	const ratingTypeLabel =
		rating.rating_level === "project" ? "Project" : "RFx";

	const projectName =
		rating.rfq_project?.name ?? rating.rfx_project?.name ?? null;
	const rfxName = rating.rfx_name ?? null;

	const submittedDate = rating.submitted_at
		? new Date(rating.submitted_at).toLocaleDateString()
		: null;

	// Group scores by category for the expanded view
	const categoryBreakdown = useMemo(() => {
		const selections = rating.category_selections ?? [];
		const scores = rating.scores ?? [];
		const comments = rating.comments ?? [];

		return selections.map((sel) => {
			const category = sel.category;
			const subCriteria = category?.sub_criteria ?? [];

			const subScores = subCriteria.map((sub) => {
				const scoreEntry = scores.find(
					(s) => s.sub_criteria_id === sub.id
				);
				const commentEntry = comments.find(
					(c) => c.sub_criteria_id === sub.id
				);
				return {
					id: sub.id,
					name: sub.name,
					score: scoreEntry?.score ?? null,
					comment: commentEntry?.comment ?? null,
				};
			});

			const validScores = subScores
				.map((s) => s.score)
				.filter((s): s is number => s !== null && s > 0);
			const avgScore =
				validScores.length > 0
					? validScores.reduce((a, b) => a + b, 0) / validScores.length
					: 0;

			return {
				categoryId: category?.id ?? sel.id,
				categoryName: category?.name ?? "Category",
				averageScore: avgScore,
				band: getBandForScore(avgScore),
				subScores,
			};
		});
	}, [rating]);

	return (
		<div
			className="rounded-lg border border-gray-200 bg-white"
			data-testid={`rating-card-${rating.id}`}
		>
			<button
				type="button"
				className="flex w-full items-center justify-between px-6 py-4 text-left"
				onClick={() => setIsExpanded(!isExpanded)}
				data-testid={`rating-card-toggle-${rating.id}`}
			>
				<div className="flex flex-1 items-center gap-4">
					<div>
						<div className="text-sm font-medium text-gray-900">
							{raterName}
						</div>
						{submittedDate && (
							<div className="text-xs text-gray-500">
								{submittedDate}
							</div>
						)}
					</div>
					<div className="flex items-center gap-3">
						<span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
							{ratingTypeLabel}
						</span>
						{projectName && (
							<span className="text-sm text-gray-700">
								{projectName}
							</span>
						)}
						{rfxName && (
							<span className="text-xs text-gray-500">
								RFx: {rfxName}
							</span>
						)}
					</div>
				</div>
				<div className="flex items-center gap-3">
					<PerformanceBadge
						score={rating.overall_score}
						band={getBandForScore(rating.overall_score ?? 0)}
						size="sm"
					/>
					{isExpanded ? (
						<FiChevronUp className="h-5 w-5 text-gray-400" />
					) : (
						<FiChevronDown className="h-5 w-5 text-gray-400" />
					)}
				</div>
			</button>

			{isExpanded && (
				<div
					className="border-t border-gray-200 px-6 py-4"
					data-testid={`rating-card-details-${rating.id}`}
				>
					<div className="mb-3 flex items-center justify-between">
						<div className="flex items-center gap-4 text-sm text-gray-500">
							<span>
								Review #{rating.review_sequence_number}
							</span>
							<span>
								{rating.frequency_type === "periodic" ? "Periodic" : "One-off"}
							</span>
						</div>
						{canDelete && onDeleteClick && (
							<button
								type="button"
								className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
								onClick={(e) => {
									e.stopPropagation();
									onDeleteClick(rating);
								}}
								data-testid={`delete-rating-${rating.id}`}
							>
								<FiTrash2 className="h-3 w-3" />
								Delete
							</button>
						)}
					</div>
					{categoryBreakdown.length > 0 ? (
						<div className="space-y-3">
							{categoryBreakdown.map((cat) => (
								<div
									key={cat.categoryId}
									className="rounded-md border border-gray-100 bg-gray-50 p-3"
								>
									<div className="mb-2 flex items-center justify-between">
										<span className="text-sm font-medium text-gray-800">
											{cat.categoryName}
										</span>
										<PerformanceBadge
											score={cat.averageScore}
											band={cat.band}
											size="sm"
										/>
									</div>
									<div className="space-y-1">
										{cat.subScores.map((sub) => (
											<div
												key={sub.id}
												className="flex items-center justify-between text-xs"
											>
												<span className="text-gray-700">
													{sub.name}
												</span>
												<div className="flex items-center gap-2">
													<span className="text-gray-600">
														{sub.score !== null ? `${sub.score}/9` : "—"}
													</span>
													{sub.comment && (
														<span className="max-w-xs truncate text-gray-500">
															{sub.comment}
														</span>
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-500">No category details available.</p>
					)}
				</div>
			)}
		</div>
	);
};
